<template>
  <div v-if="data" class="article-content flex flex-wrap flex-col lg:flex-row gap-9">
    <div v-if="data && data.toc.length && showNav" class="max-w-96">
      <h6>
        {{ $t('content') }}
      </h6>
      <nav>
        <ul>
          <li v-for="link of data.toc" :key="link.id">
            <NuxtLink :to="`#${link.id}`">
              {{ link.text }}
            </NuxtLink>
          </li>
        </ul>
      </nav>
    </div>
    <div
      v-if="showNav"
      :class="{'mx-auto': data && !data.toc.length}"
      class="block mx-auto max-w-7xl flex-1"
    >
      <h1 class="mb-14">{{ title }}</h1>
      <article v-html="data.body" />
    </div>
    <div v-else v-html="data.body" />
  </div>
</template>
<script lang="ts" setup>
// Packages
import * as markdown from 'markdown-it';
import { parse } from 'node-html-parser';
import slugify from 'slugify';

const doc = markdown.default();

// Remember the old renderer if overridden, or proxy to the default renderer.
const defaultRender = doc.renderer.rules.link_open || function (tokens, idx, options, env, self) {
  return self.renderToken(tokens, idx, options);
};


doc.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  // Add a new `target` attribute, or replace the value of the existing one.
  tokens[idx].attrSet('target', '_blank');

  // Pass the token to the default renderer.
  return defaultRender(tokens, idx, options, env, self);
};

// ---------------------------------------------
// Props
// ---------------------------------------------

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: false
  },
  showNav: {
    type: Boolean,
    required: false,
    default: false
  }
});

// ---------------------------------------------
// State
// ---------------------------------------------
const data = computed(() => renderMarkdown());



// ---------------------------------------------
// Methods
// ---------------------------------------------

function renderMarkdown() {
  const html = doc.render(props.content);

  const root = parse(html);
  const headers = root.querySelectorAll('h1, h2, h3, h4, h5, h6');

  const toc = [];

  for (const h of headers) {
    const slug = h.getAttribute('id') || slugify(h.textContent);
    h.setAttribute('id', slug);
    h.innerHTML = `<a href="#${slug}">${h.innerHTML}</a>`;

    toc.push({ text: h.textContent, path: `${slug}`, id: slug });
  }

  return { body: root.toString(), toc };
}
</script>
<style lang="scss">
.article-content {
  nav {
    ul {
      list-style: none;

      li {
        margin-bottom: 8px;
        padding: 8px;
      }
    }
  }

  ol {
    padding-right: 16px;
  }

  h3 ~ p {
    margin-bottom: 32px;
  }

  p {
    img {
      display: block;
      margin: 32px 0;
      max-width: 100%;
    }
  }
}
</style>
